import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  CheckCircle, 
  ArrowRight, 
  Globe, 
  Smartphone, 
  Cloud, 
  Code, 
  Palette, 
  Cpu, 
  Settings,
  Zap,
  Users,
  TrendingUp,
  Rocket,
  Shield
} from 'lucide-react';
import './ServiceDetail.css';

// Service Data Configuration
const servicesData = {
  // Web Development
  'web-development': {
    icon: <Globe size={48} />,
    title: 'Web Development',
    heading: 'Web Development That Drives Real Business Growth',
    description: 'We build fast, scalable, and user-focused websites that not only look great but also convert visitors into customers. From startups to growing businesses, we turn your ideas into powerful digital products.',
    ctaText: 'Let\'s Build Your Website',
    ctaAlt: 'Bring Your Idea to Life',
    whatYouGet: [
      'A fully custom-designed website tailored to your business goals',
      'Fast-loading, SEO-optimized pages for better visibility',
      'Mobile-first responsive design for all devices',
      'Secure, scalable architecture built for growth',
      'Easy-to-manage admin dashboard (if required)',
      'Ongoing support, updates, and maintenance'
    ],
    typesOfProjects: [
      { icon: '🌐', name: 'Business Websites', desc: 'For companies and startups' },
      { icon: '🛒', name: 'E-commerce platforms', desc: 'To sell products online' },
      { icon: '🚀', name: 'SaaS applications', desc: 'Web-based platforms' },
      { icon: '🎨', name: 'Portfolio websites', desc: 'For individuals and creatives' },
      { icon: '📅', name: 'Booking systems', desc: 'Appointment and scheduling' },
      { icon: '⚙️', name: 'Custom web applications', desc: 'For unique business needs' }
    ],
    features: [
      'Custom-built solutions aligned with your business goals',
      'Fully responsive across mobile, tablet, and desktop',
      'SEO-friendly structure to improve search rankings',
      'Optimized for speed and performance',
      'Built to scale as your business grows',
      'Long-term support and maintenance'
    ],
    process: [
      'Understanding Your Requirements',
      'UI/UX Design & Prototyping',
      'Development & Implementation',
      'Testing & Quality Assurance',
      'Deployment & Launch',
      'Ongoing Support & Improvements'
    ],
    whoItsFor: [
      'Startups looking to build their first product',
      'Small businesses wanting to go digital',
      'Growing companies needing scalable solutions',
      'Entrepreneurs with new product ideas'
    ],
    businessImpact: [
      { icon: <TrendingUp size={20} />, text: 'More online visibility and reach' },
      { icon: <Zap size={20} />, text: 'Increased leads and conversions' },
      { icon: <Rocket size={20} />, text: 'Faster and smoother user experience' },
      { icon: <Shield size={20} />, text: 'Strong digital presence in your industry' }
    ],
    finalCta: 'Tell us your idea — we\'ll take care of the rest.'
  },

  // Mobile App Development
  'mobile-apps': {
    icon: <Smartphone size={48} />,
    title: 'Mobile App Development',
    heading: 'Mobile Apps That Engage Users & Drive Growth',
    description: 'We build high-performance mobile applications that deliver seamless user experiences and real business results. Whether you\'re launching a new app or improving an existing one, we help you create products users love.',
    ctaText: 'Build Your App',
    ctaAlt: 'Start Your Project',
    whatYouGet: [
      'Custom iOS & Android app development',
      'Smooth, user-friendly interface',
      'High performance and scalability',
      'Secure and reliable architecture',
      'App Store & Play Store deployment support',
      'Ongoing updates and maintenance'
    ],
    typesOfProjects: [
      { icon: '🚀', name: 'Startup apps', desc: 'Launch your idea quickly' },
      { icon: '🛒', name: 'E-commerce apps', desc: 'Sell products on mobile' },
      { icon: '📅', name: 'Booking & service apps', desc: 'Appointment scheduling' },
      { icon: '💼', name: 'Business & productivity apps', desc: 'Streamline operations' }
    ],
    features: [
      'Native performance for iOS and Android',
      'Beautiful, intuitive UI/UX design',
      'Offline capabilities and push notifications',
      'Secure data storage and authentication',
      'Analytics and user tracking integration',
      'App store optimization for better discoverability'
    ],
    process: [
      'Ideation & Strategy',
      'UI/UX Design',
      'Development',
      'Testing & Quality Assurance',
      'App Store Submission',
      'Post-launch Support'
    ],
    whoItsFor: [
      'Startups launching new mobile products',
      'Businesses wanting to go mobile-first',
      'Companies needing customer engagement apps',
      'Entrepreneurs with app ideas'
    ],
    businessImpact: [
      { icon: <Users size={20} />, text: 'Increased user engagement' },
      { icon: <Zap size={20} />, text: 'Higher retention rates' },
      { icon: <TrendingUp size={20} />, text: 'Improved conversion rates' },
      { icon: <Rocket size={20} />, text: 'Faster time to market' }
    ],
    finalCta: 'Turn your app idea into reality — let\'s talk.'
  },

  // SaaS Development
  'saas-development': {
    icon: <Cloud size={48} />,
    title: 'SaaS Development',
    heading: 'Build Scalable SaaS Products That Grow With You',
    description: 'We help startups and businesses build powerful SaaS platforms from scratch — designed to scale, perform, and generate recurring revenue.',
    ctaText: 'Build Your SaaS',
    ctaAlt: 'Start Your Platform',
    whatYouGet: [
      'End-to-end SaaS product development',
      'Scalable cloud-based architecture',
      'Secure user authentication & data handling',
      'Subscription & payment integrations',
      'Admin dashboards and analytics',
      'Continuous support and scaling'
    ],
    typesOfProjects: [
      { icon: '🚀', name: 'Startup SaaS', desc: 'Building new SaaS products' },
      { icon: '💼', name: 'Business SaaS', desc: 'Digitizing operations' },
      { icon: '💰', name: 'Subscription platforms', desc: 'Launching recurring revenue products' }
    ],
    features: [
      'Multi-tenancy architecture',
      'Subscription management and billing',
      'Scalable infrastructure',
      'Analytics dashboard',
      'API-first design',
      'Secure authentication'
    ],
    process: [
      'Product Strategy',
      'Architecture Design',
      'Development',
      'Testing & Security Audit',
      'Launch & Scaling',
      'Continuous Improvement'
    ],
    whoItsFor: [
      'Startups building new SaaS products',
      'Businesses digitizing operations',
      'Founders launching subscription platforms',
      'Companies scaling existing products'
    ],
    businessImpact: [
      { icon: <TrendingUp size={20} />, text: 'Recurring revenue stream' },
      { icon: <Users size={20} />, text: 'Scalable user base' },
      { icon: <Rocket size={20} />, text: 'Faster market entry' },
      { icon: <Shield size={20} />, text: 'Enterprise-grade security' }
    ],
    finalCta: 'Ready to build your SaaS product? Let\'s make it happen.'
  },

  // UI/UX Design
  'ui-ux-design': {
    icon: <Palette size={48} />,
    title: 'UI/UX Design',
    heading: 'Design Experiences That Convert and Delight Users',
    description: 'We create intuitive and visually appealing designs that enhance user experience and increase engagement. Our focus is not just on design — but on results.',
    ctaText: 'Start Your Design',
    ctaAlt: 'Bring Your Vision to Life',
    whatYouGet: [
      'User-centered design approach',
      'Modern, clean, and engaging UI',
      'Wireframes and interactive prototypes',
      'Improved user journeys and flows',
      'Conversion-focused design strategies',
      'Design systems and guidelines'
    ],
    typesOfProjects: [
      { icon: '📱', name: 'Mobile App Design', desc: 'iOS and Android interfaces' },
      { icon: '💻', name: 'Web Design', desc: 'Responsive websites' },
      { icon: '📊', name: 'Dashboard Design', desc: 'Admin panels and analytics' },
      { icon: '🎨', name: 'Brand Identity', desc: 'Visual identity systems' }
    ],
    features: [
      'User research and testing',
      'Wireframing and prototyping',
      'Visual design and branding',
      'Usability testing',
      'Accessibility compliance',
      'Interactive prototypes'
    ],
    process: [
      'User Research',
      'Wireframing',
      'Visual Design',
      'Prototyping',
      'Usability Testing',
      'Handoff & Support'
    ],
    whoItsFor: [
      'Startups needing product design',
      'Businesses redesigning existing products',
      'Companies launching new digital products',
      'Brands needing design systems'
    ],
    businessImpact: [
      { icon: <Users size={20} />, text: 'Higher user engagement' },
      { icon: <Zap size={20} />, text: 'Better retention rates' },
      { icon: <TrendingUp size={20} />, text: 'Improved conversion rates' },
      { icon: <Rocket size={20} />, text: 'Reduced development rework' }
    ],
    finalCta: 'Let\'s design an experience your users will love.'
  },

  // AI & Machine Learning
  'ai-ml': {
    icon: <Cpu size={48} />,
    title: 'AI & Machine Learning',
    heading: 'Smart Solutions Powered by AI',
    description: 'We integrate AI into your business to automate processes, enhance decision-making, and unlock new opportunities.',
    ctaText: 'Explore AI Solutions',
    ctaAlt: 'Transform Your Business',
    whatYouGet: [
      'AI-powered chatbots and assistants',
      'Data analysis and predictive insights',
      'Automation of repetitive tasks',
      'Custom AI solutions tailored to your business',
      'Integration with your existing systems',
      'Continuous model improvement'
    ],
    typesOfProjects: [
      { icon: '🤖', name: 'AI Chatbots', desc: 'Customer support automation' },
      { icon: '📊', name: 'Recommendation Systems', desc: 'Personalized experiences' },
      { icon: '📈', name: 'Predictive Analytics', desc: 'Data-driven decisions' },
      { icon: '👁️', name: 'Computer Vision', desc: 'Image and video analysis' }
    ],
    features: [
      'Natural Language Processing',
      'Predictive modeling',
      'Computer vision capabilities',
      'Automated workflows',
      'Real-time analytics',
      'Custom model training'
    ],
    process: [
      'Business Problem Definition',
      'Data Collection & Preparation',
      'Model Selection & Training',
      'Model Evaluation',
      'Deployment & Integration',
      'Continuous Learning & Improvement'
    ],
    whoItsFor: [
      'Businesses wanting automation',
      'Companies needing predictive insights',
      'Organizations improving customer experience',
      'Startups building AI-first products'
    ],
    businessImpact: [
      { icon: <Zap size={20} />, text: 'Automated operations' },
      { icon: <TrendingUp size={20} />, text: 'Data-driven decisions' },
      { icon: <Users size={20} />, text: 'Enhanced customer experience' },
      { icon: <Rocket size={20} />, text: 'Competitive advantage' }
    ],
    finalCta: 'Ready to leverage AI for your business? Let\'s talk.'
  },

  // Cloud Solutions
  'cloud-solutions': {
    icon: <Cloud size={48} />,
    title: 'Cloud Solutions',
    heading: 'Scalable and Secure Cloud Infrastructure',
    description: 'We help businesses move to the cloud and optimize their infrastructure for performance, security, and scalability.',
    ctaText: 'Move to Cloud',
    ctaAlt: 'Optimize Your Infrastructure',
    whatYouGet: [
      'Cloud setup and migration',
      'Secure and scalable architecture',
      'Performance optimization',
      'Cost-efficient cloud management',
      'Backup and disaster recovery solutions',
      '24/7 monitoring and support'
    ],
    typesOfProjects: [
      { icon: '☁️', name: 'Cloud Migration', desc: 'Move existing apps to cloud' },
      { icon: '🏗️', name: 'Cloud Architecture', desc: 'Design scalable systems' },
      { icon: '💾', name: 'Backup Solutions', desc: 'Disaster recovery' },
      { icon: '📊', name: 'Cloud Optimization', desc: 'Cost and performance' }
    ],
    features: [
      'Multi-cloud expertise',
      'Infrastructure as Code',
      'Auto-scaling capabilities',
      'Security compliance',
      'Cost optimization',
      'Performance monitoring'
    ],
    process: [
      'Assessment',
      'Migration Strategy',
      'Implementation',
      'Testing',
      'Optimization',
      'Ongoing Management'
    ],
    whoItsFor: [
      'Businesses migrating to cloud',
      'Companies needing scalable infrastructure',
      'Organizations requiring disaster recovery',
      'Startups building cloud-native apps'
    ],
    businessImpact: [
      { icon: <Zap size={20} />, text: 'Reduced infrastructure costs' },
      { icon: <Rocket size={20} />, text: 'Better performance' },
      { icon: <Shield size={20} />, text: 'Easy scalability' },
      { icon: <TrendingUp size={20} />, text: 'Improved reliability' }
    ],
    finalCta: 'Ready to take your business to the cloud? Let\'s start.'
  },

  // DevOps Services
  'devops': {
    icon: <Settings size={48} />,
    title: 'DevOps Services',
    heading: 'Faster Development, Smarter Deployment',
    description: 'We streamline your development and deployment processes to deliver faster, more reliable software releases.',
    ctaText: 'Optimize Your Workflow',
    ctaAlt: 'Speed Up Delivery',
    whatYouGet: [
      'Automated CI/CD pipelines',
      'Faster and reliable deployments',
      'Infrastructure automation',
      'Monitoring and performance tracking',
      'Reduced downtime and errors',
      'Improved team efficiency'
    ],
    typesOfProjects: [
      { icon: '🔧', name: 'CI/CD Setup', desc: 'Automated deployments' },
      { icon: '🏗️', name: 'Infrastructure as Code', desc: 'Automated infrastructure' },
      { icon: '📊', name: 'Monitoring', desc: 'Performance tracking' },
      { icon: '🔄', name: 'Pipeline Optimization', desc: 'Faster delivery' }
    ],
    features: [
      'Continuous Integration',
      'Continuous Delivery',
      'Infrastructure Automation',
      'Monitoring & Alerting',
      'Security scanning',
      'Performance optimization'
    ],
    process: [
      'Assessment',
      'Strategy Development',
      'Tool Selection',
      'Pipeline Setup',
      'Monitoring Implementation',
      'Continuous Optimization'
    ],
    whoItsFor: [
      'Teams wanting faster deployment',
      'Companies improving software quality',
      'Organizations reducing downtime',
      'Startups scaling development'
    ],
    businessImpact: [
      { icon: <Rocket size={20} />, text: 'Faster time to market' },
      { icon: <Zap size={20} />, text: 'Improved product quality' },
      { icon: <Users size={20} />, text: 'Better team efficiency' },
      { icon: <Shield size={20} />, text: 'Reduced downtime' }
    ],
    finalCta: 'Let\'s make your development process faster and smarter.'
  },

  // MVP Development
  'mvp-development': {
    icon: <Rocket size={48} />,
    title: 'MVP Development',
    heading: 'Launch Your Idea Faster with MVP Development',
    description: 'We help startups turn ideas into working products quickly, so you can test, validate, and scale with confidence.',
    ctaText: 'Build Your MVP',
    ctaAlt: 'Launch Your Idea',
    whatYouGet: [
      'Rapid development of core features',
      'Cost-effective product launch',
      'Scalable foundation for future growth',
      'Feedback-driven improvements',
      'Faster time to market',
      'Investment-ready product'
    ],
    typesOfProjects: [
      { icon: '🚀', name: 'Startup MVP', desc: 'Validate your idea' },
      { icon: '📱', name: 'App MVP', desc: 'Test mobile concepts' },
      { icon: '💻', name: 'Web Platform MVP', desc: 'Launch quickly' },
      { icon: '💰', name: 'Investment Ready', desc: 'Attract funding' }
    ],
    features: [
      'Lean development approach',
      'Core feature focus',
      'Fast iterations',
      'User feedback integration',
      'Scalable architecture',
      'Investment-ready demo'
    ],
    process: [
      'Idea Validation',
      'Feature Prioritization',
      'Rapid Prototyping',
      'Agile Development',
      'User Testing',
      'Launch & Iterate'
    ],
    whoItsFor: [
      'Startups validating ideas',
      'Entrepreneurs launching new products',
      'Businesses testing new markets',
      'Founders seeking investment'
    ],
    businessImpact: [
      { icon: <Rocket size={20} />, text: 'Faster time to market' },
      { icon: <Zap size={20} />, text: 'Cost-effective launch' },
      { icon: <Users size={20} />, text: 'Early user feedback' },
      { icon: <TrendingUp size={20} />, text: 'Investment ready' }
    ],
    finalCta: 'Ready to turn your idea into reality? Let\'s build your MVP.'
  },

  // API Development
  'api-development': {
    icon: <Code size={48} />,
    title: 'API Development',
    heading: 'Robust APIs That Power Your Applications',
    description: 'We design and develop secure, scalable APIs that connect your systems and enable seamless communication between applications.',
    ctaText: 'Build Your API',
    ctaAlt: 'Connect Your Systems',
    whatYouGet: [
      'Custom API development',
      'Secure and scalable integrations',
      'Third-party API integrations',
      'High performance and reliability',
      'Easy documentation and maintenance',
      'Comprehensive testing'
    ],
    typesOfProjects: [
      { icon: '🔌', name: 'REST APIs', desc: 'Standard web APIs' },
      { icon: '📊', name: 'GraphQL APIs', desc: 'Flexible queries' },
      { icon: '🔗', name: 'Third-party Integration', desc: 'Connect external services' },
      { icon: '💳', name: 'Payment Gateways', desc: 'Stripe, Razorpay, etc.' }
    ],
    features: [
      'RESTful & GraphQL APIs',
      'High performance',
      'Security best practices',
      'Comprehensive documentation',
      'Scalable architecture',
      'Easy integration'
    ],
    process: [
      'Requirements Analysis',
      'API Design',
      'Development',
      'Testing & Security',
      'Documentation',
      'Deployment & Monitoring'
    ],
    whoItsFor: [
      'Companies needing system integration',
      'Startups building API-first products',
      'Businesses connecting to third parties',
      'Teams requiring scalable APIs'
    ],
    businessImpact: [
      { icon: <Zap size={20} />, text: 'Seamless integration' },
      { icon: <Rocket size={20} />, text: 'Faster development' },
      { icon: <Shield size={20} />, text: 'Secure data transfer' },
      { icon: <TrendingUp size={20} />, text: 'Scalable architecture' }
    ],
    finalCta: 'Ready to build powerful APIs? Let\'s connect your systems.'
  }
};

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = servicesData[serviceId];

  if (!service) {
    return (
      <div className="service-detail">
        <div className="container">
          <div className="error-message" style={{ textAlign: 'center', padding: '100px 0' }}>
            <h2>Service not found</h2>
            <p>The service you're looking for doesn't exist or has been moved.</p>
            <Link to="/services" className="btn">Back to Services</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="service-detail">
      {/* Hero Section */}
      <section className="service-hero">
        <div className="container">
          <div className="service-hero-content">
            <div className="service-icon-large">{service.icon}</div>
            <h1>{service.heading}</h1>
            <p>{service.description}</p>
            <div className="hero-buttons">
              <Link to="/contact" className="btn btn-primary">
                {service.ctaText} <ArrowRight size={18} />
              </Link>
              <Link to="/contact" className="btn btn-outline">
                {service.ctaAlt}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* What You Get Section */}
      <section className="what-you-get section">
        <div className="container">
          <h2 className="section-title">What You Get With Our <span className="blue-text">{service.title}</span></h2>
          <div className="benefits-grid">
            {service.whatYouGet.map((item, index) => (
              <div key={index} className="benefit-item">
                <CheckCircle size={24} className="check-icon" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of Projects Section */}
      <section className="types-projects section">
        <div className="container">
          <h2 className="section-title">Types of <span className="blue-text">Projects</span> We Build</h2>
          <div className="types-grid">
            {service.typesOfProjects.map((type, index) => (
              <div key={index} className="type-card">
                <span className="type-icon">{type.icon}</span>
                <h3>{type.name}</h3>
                <p>{type.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Our Solutions Stand Out Section */}
      <section className="features-section section">
        <div className="container">
          <h2 className="section-title">Why Our <span className="blue-text">Solutions Stand Out</span></h2>
          <div className="features-grid">
            {service.features.map((feature, index) => (
              <div key={index} className="feature-item">
                <CheckCircle size={20} className="check-icon-small" />
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section section">
        <div className="container">
          <h2 className="section-title">How We Turn Your Idea Into a <span className="blue-text">Live Product</span></h2>
          <div className="process-steps">
            {service.process.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{index + 1}</div>
                <div className="step-content">
                  <h3>{step}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For Section */}
      <section className="who-its-for section">
        <div className="container">
          <h2 className="section-title">Who <span className="blue-text">We Work With</span></h2>
          <div className="who-grid">
            {service.whoItsFor.map((item, index) => (
              <div key={index} className="who-card">
                <Users size={28} className="who-icon" />
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Business Impact Section */}
      <section className="business-impact section">
        <div className="container">
          <h2 className="section-title">What This Means for Your <span className="blue-text">Business</span></h2>
          <div className="impact-grid">
            {service.businessImpact.map((impact, index) => (
              <div key={index} className="impact-card">
                <div className="impact-icon">{impact.icon}</div>
                <p>{impact.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta">
        <div className="container">
          <div className="cta-content">
            <h2>{service.ctaText}</h2>
            <p>{service.finalCta}</p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Start Your Project <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;